import {
  BudgetItemTypeEnum,
  type TimeAndMaterialsInvoiceDataQuery,
  type TimeMaterialsBudgetItems,
  type TimeMaterialsCommissionData,
} from "@src/__generated__/graphql";
import type { InvoiceStore } from "@src/components/modules/invoices/Create/store";
import { NumberModel } from "@src/utils/models";
import { action, computed, makeObservable, observable } from "mobx";
import type { InvoiceItemArgs } from "../../../../form";
import { TimeAndMaterialsBudgetItemForm } from "./forms";
import type {
  GenerateFromOption,
  TimeAndMaterialsProjectCardStore,
} from "./store";

export type TimeAndMaterialsBudgetItem = NonNullable<TimeMaterialsBudgetItems>;

export class TimeAndMaterialsTableState {
  readonly tableKey: string;

  @observable tableData: TimeAndMaterialsBudgetItemForm[] = [];
  @observable selectedIds: TimeAndMaterialsBudgetItem["id"][] = [];

  constructor(
    tableKey: string,
    private store: TimeAndMaterialsProjectCardStore,
    private invoiceStore: InvoiceStore,
  ) {
    makeObservable(this);

    this.tableKey = tableKey;
  }

  @computed get allIds(): TimeAndMaterialsBudgetItem["id"][] {
    return this.tableData.flatMap(({ id, __subRows }) => {
      const allIds = [id];

      for (const subRow of __subRows) {
        allIds.push(subRow.id);
      }

      return allIds;
    });
  }

  @computed get isGenerateFromGroupingDisabled(): boolean {
    return this.tableData.some(({ amountToInvoice }) => amountToInvoice.dirty);
  }

  @computed get selectedSubrowIds(): string[] {
    const ids: string[] = [];

    for (const row of this.tableData) {
      for (const subRow of row.__subRows) {
        if (this.selectedIds.includes(row.id)) {
          ids.push(subRow.id);
          continue;
        }

        if (!this.selectedIds.includes(subRow.id)) continue;
        ids.push(subRow.id);
      }
    }
    return ids;
  }

  @computed get totalToInvoice(): number {
    let amountToInvoice = 0;

    for (const row of this.tableData) {
      amountToInvoice += row.amountToInvoice.$ ?? 0;
    }

    return amountToInvoice;
  }

  @computed get commissionData(): TimeMaterialsCommissionData[] {
    const data: TimeMaterialsCommissionData[] = [];

    this.iterateOverSelectedRows((row) => {
      if (!row.commission) return;
      data.push({
        expense_budget_item_id: row.id,
        commission: row.commission.$ ?? 0,
      });
    });

    return data;
  }

  @computed get currencyCode(): string {
    return this.store.appStore.workspaceStore.settings?.currency.code ?? "EUR";
  }

  @action setTableData(
    budgetItems:
      | TimeAndMaterialsInvoiceDataQuery["timeMaterialsInvoiceData"]["ourWorkBudgetItems"]
      | TimeAndMaterialsInvoiceDataQuery["timeMaterialsInvoiceData"]["expenseBudgetItems"],
  ) {
    this.tableData = budgetItems.map(
      (item) => new TimeAndMaterialsBudgetItemForm(item, this.store.appStore),
    );
  }

  @action setSelectedIds(ids: string[]) {
    this.selectedIds = ids;
  }

  @action.bound handleSelectRows(rowIds: string[]) {
    this.setSelectedIds(rowIds);

    for (const row of this.tableData) {
      let amountToInvoice = row.getSelectedAmount(rowIds);

      if (row.isRowOrChildSelected(rowIds) && !row.commission?.dirty) {
        row.setCommission(row.default_commission);
        amountToInvoice = row.addPercent(
          row.getSelectedAmount(rowIds),
          row.commission?.$ ?? 0,
        );
      } else if (row.isRowOrChildSelected(rowIds) && row.commission?.dirty) {
        amountToInvoice = row.addPercent(
          row.getSelectedAmount(rowIds),
          row.commission.$ ?? 0,
        );
      } else {
        row.setCommission(null);
      }

      if (
        row.amountToInvoice.dirty &&
        row.amountToInvoice.$ !== amountToInvoice
      )
        continue;

      row.setAmountToInvoice(amountToInvoice, true);
    }
  }

  @action.bound setAmountsFromSplits(
    splits: Array<
      | { our_work_budget_item_id?: string | null; amount?: number | null }
      | { expense_budget_item_id?: string | null; amount?: number | null }
    >,
  ) {
    for (const row of this.tableData) {
      const match = splits.find((split) => {
        if ("our_work_budget_item_id" in split) {
          return split.our_work_budget_item_id === row.id;
        } else if ("expense_budget_item_id" in split) {
          return split.expense_budget_item_id === row.id;
        } else {
          return false;
        }
      });

      if (!match) continue;
      if (match.amount === row.amountToInvoice.$) continue;
      row.amountToInvoice.onChange(match.amount ?? 0);
    }
  }

  @action.bound setCommissionData(
    commissionData: TimeMaterialsCommissionData[],
  ) {
    for (const item of commissionData) {
      const match = this.tableData.find(
        (row) => row.id === item.expense_budget_item_id,
      );
      if (!match) continue;
      match.setCommission(
        item.commission,
        item.commission !== match.default_commission,
      );
    }
  }

  iterateOverSelectedRows(
    callback: (row: TimeAndMaterialsBudgetItemForm) => void,
  ) {
    for (const row of this.tableData) {
      const isRowSelected = this.selectedIds.includes(row.id);
      const isSubRowSelected = row.__subRows.some((subRow) =>
        this.selectedIds.includes(subRow.id),
      );

      if (!isRowSelected && !isSubRowSelected) continue;
      callback(row);
    }
  }

  generateInvoiceItems(
    option: GenerateFromOption,
    callback: (item: InvoiceItemArgs) => void,
    budgetItemType: BudgetItemTypeEnum,
  ) {
    const denomination =
      this.store.appStore.workspaceStore.settings?.currency.denomination ?? 2;

    if (option === "budget-item") {
      this.iterateOverSelectedRows((row) => {
        const selectedSubRowsNames = row.__subRows
          .filter(
            ({ id }) =>
              this.selectedIds.includes(id) ||
              this.selectedIds.includes(row.id),
          )
          .map(({ name }) => name)
          .join(", ");

        const amount = new NumberModel(
          row.amountToInvoice.$ ?? 0,
          denomination,
        );

        callback({
          description: selectedSubRowsNames.length
            ? `${row.title} - ${selectedSubRowsNames}`
            : row.title,
          amount_in_currency: amount,
          unit_price: amount.value.toString(),
          quantity: "1",
          accounting_account_id:
            this.store.appStore.workspaceStore.getAccountingAccountForBudgetCategory(
              row.budgetCategoryId,
              budgetItemType,
            )?.id ?? "",
          cost_center_id: undefined,
          tag_ids: [],
          vat_percent_informative:
            this.invoiceStore.form.vat_percent_informative.value,
        });
      });
    } else {
      const canUseUnitPrice =
        budgetItemType === BudgetItemTypeEnum.OurWorkBudgetItem;

      this.iterateOverSelectedRows((row) => {
        for (const subRow of row.__subRows) {
          if (!this.selectedIds.includes(subRow.id)) continue;
          const amount = new NumberModel(
            row.addPercent(subRow.amount ?? 0, row.commission?.$ ?? 0),
            denomination,
          );

          const quantity = canUseUnitPrice
            ? (subRow.tracked_time ?? 3600) / 3600
            : 1;
          const unitPrice = amount.value / quantity;

          callback({
            description: `${row.title} - ${subRow.name}`,
            amount_in_currency: amount,
            unit_price: unitPrice.toFixed(2),
            quantity: quantity.toFixed(2),
            accounting_account_id:
              this.store.appStore.workspaceStore.getAccountingAccountForBudgetCategory(
                row.budgetCategoryId,
                budgetItemType,
              )?.id ?? "",
            cost_center_id: undefined,
            tag_ids: [],
            vat_percent_informative:
              this.invoiceStore.form.vat_percent_informative.value,
          });
        }
      });
    }
  }

  getBudgetSplits(type: "expense" | "our_work") {
    return this.tableData
      .filter(
        (row) =>
          this.selectedIds.includes(row.id) ||
          row.__subRows.some((subRow) => this.selectedIds.includes(subRow.id)),
      )
      .map((row) => ({
        [`${type}_budget_item_id`]: row.id,
        amount: row.amountToInvoice.$ ?? 0,
      }));
  }
}
